// Shared helpers for normalizing GitLab-sourced URLs, HTML, and author identity.
//
// These previously existed as independent copies in useMRNotes.js,
// useMergeRequests.js, and the Needs Response view. Scheme-sensitive URL
// rewriting and bot detection are security-adjacent, so they live in one place
// to guarantee every consumer applies the same rules.

import {
	AI_CODE_REVIEWER_USERNAME,
	GITLAB_BASE_URL,
	MURLOC_REVIEWER_USERNAME,
} from "./constants";

// Absolutize a GitLab avatar/asset URL. GitLab returns root-relative avatar
// paths (e.g. "/uploads/..") that must be resolved against the GitLab origin,
// not the dashboard origin. Returns null for empty input.
export const withAvatarBase = (url) => {
	if (!url) return null;
	return url.startsWith("https://") ? url : `${GITLAB_BASE_URL}${url}`;
};

// GitLab returns bodyHtml with root-relative links/images (e.g. href="/group/..",
// src="/uploads/..") that would otherwise resolve against the dashboard origin.
// Rewrite them to absolute GitLab URLs so they work from the dashboard. Only
// root-relative href/src values are touched; the scheme is never changed.
//
// GitLab also lazy-loads media: the real URL sits in data-src and src holds a
// transparent placeholder GIF that GitLab's own JS swaps out. Without that JS
// the image renders as an empty box, so promote data-src to src first.
const LAZY_MEDIA_TAG = /<(img|video|source)\b[^>]*\sdata-src="[^"]*"[^>]*>/g;

const promoteLazySrc = (tag) => {
	const dataSrc = tag.match(/\sdata-src="([^"]*)"/)[1];
	const src = tag.match(/\ssrc="([^"]*)"/)?.[1];
	if (src && !src.startsWith("data:")) return tag;
	return tag
		.replace(/\ssrc="[^"]*"/, "")
		.replace(/\sdata-src="[^"]*"/, ` src="${dataSrc}"`);
};

export const rewriteRelativeUrls = (html) => {
	if (!html) return "";
	return html
		.replace(LAZY_MEDIA_TAG, promoteLazySrc)
		.replace(/(href|src)="\/(?!\/)/g, `$1="${GITLAB_BASE_URL}/`)
		.replace(/(href|src)='\/(?!\/)/g, `$1='${GITLAB_BASE_URL}/`);
};

export const isAi = (author) => author?.username === AI_CODE_REVIEWER_USERNAME;

export const isMurloc = (author) =>
	author?.username === MURLOC_REVIEWER_USERNAME;

// Atlantis is a Terraform policy/CI bot, not a human reviewer. GitLab does not
// always flag its account with `bot: true`, so match on identity as well.
export const isAtlantis = (author) => {
	const username = author?.username?.toLowerCase() || "";
	const name = author?.name?.toLowerCase() || "";
	return username.includes("atlantis") || name === "atlantis";
};

// True for any non-human comment author: GitLab-flagged bots, the AI reviewer,
// murloc, Renovate, and Atlantis. Used to decide whether a comment counts as a
// human message awaiting a response.
export const isAutomated = (author) => {
	if (!author) return false;
	const username = author.username?.toLowerCase() || "";
	return (
		Boolean(author.bot) ||
		isAi(author) ||
		isMurloc(author) ||
		isAtlantis(author) ||
		username === "renovate"
	);
};
